import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link, useParams, Prompt } from 'react-router-dom'
import { CalendarIcon } from '@heroicons/react/outline'
import { WORKFORCE_SCHEDULE } from '../../navigation/constants'
import { useTranslation } from 'react-i18next'
import { PageView, PageTopBar, Notification } from '../../components'
import { Loader } from '../../components/Loader'
import { fetchWorkShifts, fetchShiftAttendance, saveShiftAttendance } from '../../services/workforceServices'
import { WorkforceTabs } from './WorkforceTabs'
import { AttendanceRow } from './components/AttendanceRow'
import { ExtraShiftPicker } from './components/ExtraShiftPicker'
import { AttendanceSaveBar } from './components/AttendanceSaveBar'
import { extractBackendError, invalidateWorkforce } from '../../utils/workforceUtils'

const todayIso = () => {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const formatTime = s => (s || '').slice(0, 5)

export const AttendanceContainer = () => {
  const { warehouse_id } = useParams()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [date, setDate] = useState(todayIso())
  const [activeShiftId, setActiveShiftId] = useState(null)
  const [primaryMap, setPrimaryMap] = useState({}) // user_warehouse_id -> { present: bool }
  const [extras, setExtras] = useState([]) // [{ user_warehouse_id, operator_type, contract_type, full_name, extra_hours, touched }]
  const [dirty, setDirty] = useState(false)
  const [rowErrors, setRowErrors] = useState({}) // user_warehouse_id -> message
  const [toast, setToast] = useState(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [dateError, setDateError] = useState('')

  const { data: shiftsData, isLoading: shiftsLoading } = useQuery(
    ['workforce', warehouse_id, 'shifts', { include_inactive: false }],
    () => fetchWorkShifts(warehouse_id),
    { refetchOnWindowFocus: false },
  )
  const shifts = useMemo(() => {
    const raw = Array.isArray(shiftsData) ? shiftsData : shiftsData?.work_shifts || shiftsData?.shifts || []
    return raw.filter(s => s.active !== false)
  }, [shiftsData])

  useEffect(() => {
    if (!activeShiftId && shifts.length > 0) setActiveShiftId(shifts[0].id)
  }, [shifts, activeShiftId])

  const {
    data: attendance,
    isLoading: attLoading,
    isFetching,
    error: attendanceError,
    isError: attendanceIsError,
  } = useQuery(['workforce', warehouse_id, 'attendance', activeShiftId, date], () => fetchShiftAttendance(warehouse_id, activeShiftId, { date }), {
    enabled: !!activeShiftId,
    keepPreviousData: false,
    retry: false,
  })

  const notScheduled = useMemo(() => {
    if (!attendanceIsError) return false
    const status = attendanceError?.response?.status
    const msg = attendanceError?.response?.data?.error || ''
    return status === 422 && /not scheduled/i.test(msg)
  }, [attendanceIsError, attendanceError])

  // Hydrate local state when attendance loads
  useEffect(() => {
    if (!attendance) return
    const raw = Array.isArray(attendance.operators) ? attendance.operators : []
    const primary = {}
    const ex = []
    for (const op of raw) {
      const uwId = op.user_warehouse_id
      const enriched = {
        user_warehouse_id: uwId,
        full_name: op.user_name,
        operator_type: op.operator_type,
        contract_type: op.contract_type,
        present: !!op.present,
        extra_hours: op.extra_hours,
        // A saved row whose operator is no longer on this shift that day (moved or terminated
        // since). A platform that does not send the field only lists roster operators.
        on_roster: op.on_roster !== false,
      }
      if (op.is_extra_shift) ex.push({ ...enriched, touched: false })
      else primary[uwId] = enriched
    }
    setPrimaryMap(primary)
    setExtras(ex)
    setDirty(false)
    setRowErrors({})
  }, [attendance])

  const togglePresent = uwId => {
    setPrimaryMap(prev => ({ ...prev, [uwId]: { ...prev[uwId], present: !prev[uwId].present } }))
    setDirty(true)
  }

  const updateExtraHours = (uwId, v) => {
    setExtras(prev => prev.map(e => (e.user_warehouse_id === uwId ? { ...e, extra_hours: v === '' ? '' : Number(v), touched: true } : e)))
    setDirty(true)
  }

  const removeExtra = uwId => {
    setExtras(prev => prev.filter(e => e.user_warehouse_id !== uwId))
    setDirty(true)
  }

  const addExtra = ({ operator, extra_hours }) => {
    const uwId = operator.id
    if (primaryMap[uwId]) {
      setToast({ type: 'error', title: t('workforce.attendance.errors.duplicate_operator') })
      setPickerOpen(false)
      return
    }
    if (extras.some(e => e.user_warehouse_id === uwId)) {
      setToast({ type: 'error', title: t('workforce.attendance.errors.duplicate_operator') })
      setPickerOpen(false)
      return
    }
    setExtras(prev => [
      ...prev,
      {
        user_warehouse_id: uwId,
        full_name: operator.user_name || operator.user?.full_name || operator.full_name,
        operator_type: operator.operator_type,
        contract_type: operator.contract_type,
        present: true,
        extra_hours,
        touched: true,
      },
    ])
    setDirty(true)
    setPickerOpen(false)
  }

  const saveMutation = useMutation(
    () => {
      const primary = Object.values(primaryMap).map(r => ({
        user_warehouse_id: r.user_warehouse_id,
        present: r.present,
        is_extra_shift: false,
      }))
      const extraRecords = extras
        .filter(e => e.touched)
        .map(e => ({
          user_warehouse_id: e.user_warehouse_id,
          present: true,
          is_extra_shift: true,
          extra_hours: Number(e.extra_hours) || 0,
        }))
      return saveShiftAttendance(warehouse_id, activeShiftId, {
        date,
        attendance: [...primary, ...extraRecords],
      })
    },
    {
      onSuccess: () => {
        invalidateWorkforce(queryClient, warehouse_id, ['attendance', 'costs', 'costs-range'], { shift_id: activeShiftId })
        setDirty(false)
        setRowErrors({})
        setToast({ type: 'success', title: t('workforce.attendance.saved'), description: t('workforce.attendance.saved_note') })
      },
      onError: err => {
        const { message } = extractBackendError(err)
        // Try to attach row-level errors from "Attendance for operator N: ..." pattern
        const match = /operator\s+(\d+)/i.exec(message || '')
        if (match) {
          setRowErrors({ [Number(match[1])]: message })
        }
        setToast({ type: 'error', title: t('workforce.errors.save_failed'), description: message })
      },
    },
  )

  const onChangeDate = v => {
    if (v > todayIso()) {
      setDateError(t('workforce.attendance.errors.future_date'))
      return
    }
    setDateError('')
    setDate(v)
  }

  const presentCount = Object.values(primaryMap).filter(r => r.present).length
  const totalCount = Object.values(primaryMap).length
  const extraCount = extras.length
  const excludeIds = [...Object.keys(primaryMap).map(Number), ...extras.map(e => e.user_warehouse_id)]

  const activeShift = shifts.find(s => s.id === activeShiftId)
  const overnight = activeShift && formatTime(activeShift.end_time) < formatTime(activeShift.start_time)

  return (
    <>
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">{t('workforce.attendance.title')}</div>
          </PageTopBar>
        }
        topMenuFullWidth={true}
        childrenFullWidth={true}
      >
        <WorkforceTabs />
        <Prompt when={dirty} message={t('workforce.attendance.unsaved_prompt')} />

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('workforce.attendance.date')}</label>
            <input
              type="date"
              value={date}
              onChange={e => onChangeDate(e.target.value)}
              max={todayIso()}
              className="border border-gray-300 rounded-md text-sm px-3 py-2"
            />
            {dateError && <p className="mt-1 text-xs text-red-600">{dateError}</p>}
          </div>
        </div>

        {shiftsLoading ? (
          <div className="mt-6">
            <Loader show={true} />
          </div>
        ) : shifts.length === 0 ? (
          <div className="mt-6 p-8 text-center text-gray-500 border rounded bg-white">{t('workforce.attendance.no_shifts')}</div>
        ) : (
          <>
            <div className="mt-4 border-b border-gray-200">
              <nav className="-mb-px flex gap-4 overflow-x-auto">
                {shifts.map(s => {
                  const isActive = s.id === activeShiftId
                  const shiftOvernight = formatTime(s.end_time) < formatTime(s.start_time)
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        if (dirty && !window.confirm(t('workforce.attendance.unsaved_prompt'))) return
                        setActiveShiftId(s.id)
                      }}
                      className={`whitespace-nowrap py-3 px-2 border-b-2 text-sm font-medium ${isActive ? 'border-yellow-500 text-yellow-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                      <div>{s.name}</div>
                      <div className="text-xs text-gray-500">
                        {formatTime(s.start_time)} → {formatTime(s.end_time)}
                        {shiftOvernight && <span> ({t('workforce.shifts.next_day')})</span>}
                      </div>
                    </button>
                  )
                })}
              </nav>
            </div>

            {overnight && (
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">{t('workforce.attendance.overnight_hint')}</div>
            )}

            {attLoading ? (
              <div className="mt-6">
                <Loader show={true} />
              </div>
            ) : notScheduled ? (
              <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center">
                <CalendarIcon className="mx-auto h-10 w-10 text-gray-300" />
                <h3 className="mt-3 text-sm font-semibold text-gray-900">{t('workforce.attendance.not_scheduled_title')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('workforce.attendance.not_scheduled_body', { date })}</p>
                <Link
                  to={WORKFORCE_SCHEDULE.replace(':warehouse_id', warehouse_id)}
                  className="mt-4 inline-flex items-center rounded-md bg-yellow-500 px-3 py-2 text-sm font-medium text-white hover:bg-yellow-600"
                >
                  {t('workforce.attendance.schedule_this_shift')}
                </Link>
              </div>
            ) : (
              <div className="mt-4 bg-white border rounded-lg">
                <div className="px-3 py-2 border-b bg-gray-50 text-xs font-medium text-gray-600 uppercase">{t('workforce.attendance.assigned_operators')}</div>
                {Object.values(primaryMap).length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">{t('workforce.attendance.no_assigned')}</div>
                ) : (
                  Object.values(primaryMap).map(r => (
                    <AttendanceRow
                      key={r.user_warehouse_id}
                      row={r}
                      error={rowErrors[r.user_warehouse_id]}
                      isExtra={false}
                      onTogglePresent={() => togglePresent(r.user_warehouse_id)}
                    />
                  ))
                )}

                <div className="px-3 py-2 border-y bg-gray-50 text-xs font-medium text-gray-600 uppercase flex items-center justify-between">
                  <span>{t('workforce.attendance.extra_shift')}</span>
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="text-xs border border-gray-300 rounded-md px-2 py-1 text-gray-700 hover:bg-gray-50 bg-white normal-case"
                  >
                    + {t('workforce.attendance.add_extra')}
                  </button>
                </div>
                {extras.length === 0 ? (
                  <div className="p-4 text-center text-xs text-gray-400">{t('workforce.attendance.no_extras')}</div>
                ) : (
                  extras.map(e => (
                    <AttendanceRow
                      key={`extra-${e.user_warehouse_id}`}
                      row={e}
                      error={rowErrors[e.user_warehouse_id]}
                      isExtra={true}
                      onChangeExtraHours={v => updateExtraHours(e.user_warehouse_id, v)}
                      onRemoveExtra={() => removeExtra(e.user_warehouse_id)}
                    />
                  ))
                )}
              </div>
            )}
          </>
        )}

        {isFetching && !attLoading && <div className="mt-2 text-xs text-gray-500">{t('workforce.updating')}</div>}
      </PageView>

      <AttendanceSaveBar
        presentCount={presentCount}
        totalCount={totalCount}
        extraCount={extraCount}
        hasUnsaved={dirty}
        isSaving={saveMutation.isLoading}
        onSave={() => saveMutation.mutate()}
      />

      {pickerOpen && <ExtraShiftPicker warehouse_id={warehouse_id} excludeIds={excludeIds} onPick={addExtra} onClose={() => setPickerOpen(false)} />}

      {toast && <Notification show={!!toast} setShow={() => setToast(null)} type={toast.type} title={toast.title} description={toast.description} />}
    </>
  )
}
